import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  OnChanges,
  SimpleChanges,
  input,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { ModalService } from 'src/app/_services/modal.service';
import { ButtonComponent } from '../../buttons/button/button.component';
import { UnderlinedTextareaFieldComponent } from '../../forms/underlined-textarea-field/underlined-textarea-field.component';
import { ErrorLog } from 'src/app/_models/error-log';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';

@Component({
    selector: 'app-error-log-modal',
    templateUrl: './error-log-modal.component.html',
    styleUrl: './error-log-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UnderlinedTextareaFieldComponent,
        ButtonComponent,
        MatIconButtonComponent,
        MatIcon,
        DatePipe,
    ]
})
export class ErrorLogModalComponent implements OnInit, OnDestroy, OnChanges {
  private readonly modalService = inject(ModalService);
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly element: HTMLElement;

  minErrorLogNoteLength = NotesParameterRanges.ERROR_LOG_NOTES.minNoteLength;
  maxErrorLogNoteLength = NotesParameterRanges.ERROR_LOG_NOTES.maxNoteLength;

  notesField = new FormBase({
    key: 'notes',
    label: 'Notes',
    type: 'text',
    placeholder: 'Enter note here...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  // Initialize form with disabled state set at creation time (following Angular's recommendation)
  notesForm = this.fb.group({
    notes: [
      { value: '', disabled: false },
      [Validators.maxLength(this.maxErrorLogNoteLength)]
    ],
  });

  // Add a getter for easy form control access
  get f() {
    return this.notesForm.controls;
  }

  /** Modal id (required)*/
  id = input<string>();

  /** Error log object (required) */
  errorLog = input<ErrorLog>();

  /** Modal title (required) */
  titleText = input<string>();

  /** Whether the user can add/update notes (required) */
  permissionToEdit = input<boolean>();

  /** Has the error log been updated successfully (required) */
  errorLogUpdated = input<boolean>(false);

  /** Is the modal processing (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Output event for clicked button on modal */
  modalButtonClicked = output<string>();

  constructor() {
    const el = this.elementRef;
    this.element = el.nativeElement;
  }

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const modal = this;

    // Close modal on background click - only if not processing a rejection
    if (!this.isProcessing) {
      this.element.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.className === 'modal--container') {
          modal.close();
        }
      });
    }
    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);
    document.body.appendChild(this.element);
  }

  buttonEventHandler(): void {
      this.modalButtonClicked.emit(this.notesForm.value.notes);
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset notes form to the current error log notes or empty string
    const el = this.errorLog();
    this.notesForm.patchValue({ notes: el?.notes || '' });
    this.element.style.display = 'none';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['errorLog'] && this.notesForm) {
      const newValue = changes['errorLog'].currentValue as ErrorLog;
      this.notesForm.patchValue({ notes: newValue?.notes || '' });
    }
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
